/**
 * HelpBanner — Dismissible info card for first-use feature explanations.
 *
 * Generic surface that any feature can drop in via `useDismissibleHelp`. The
 * banner is informational (not an alert), so it uses the muted primary tones
 * rather than warning/error styling. Dismissal is the parent's concern; this
 * component just renders the chrome and the close affordance.
 */
import { Info, X } from 'lucide-react';

import { cn } from '@/lib/utils';

interface HelpBannerProps {
  title: string;
  children: React.ReactNode;
  onDismiss: () => void;
  className?: string;
}

export function HelpBanner({ title, children, onDismiss, className }: HelpBannerProps) {
  return (
    <div
      role="region"
      aria-label={title}
      className={cn(
        'relative rounded-md border border-primary/20 bg-primary/5 p-4 pr-10 text-sm',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="space-y-1.5">
          <p className="font-medium text-foreground">{title}</p>
          <div className="text-muted-foreground leading-relaxed">{children}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Dismiss ${title}`}
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
